import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Mail';
import StadiumIcon from '@mui/icons-material/Stadium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PageHeader from '../components/PageHeader';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import type { Meet, Notification, Student, ScheduleEntry } from '../api/types';
import dayjs from 'dayjs';

export default function DashboardPage() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';

  const meetsQuery = useQuery<Meet[]>({
    queryKey: ['meets', 'list'],
    enabled: !isParent,
    queryFn: async () => (await api.get('/meets')).data,
  });

  const studentsQuery = useQuery<Student[]>({
    queryKey: ['students', 'list'],
    enabled: !isParent,
    queryFn: async () => (await api.get('/students')).data,
  });

  const notifsQuery = useQuery<Notification[]>({
    queryKey: ['notifications', 'list'],
    queryFn: async () => (await api.get('/notifications')).data,
  });

  const childrenQuery = useQuery<Student[]>({
    queryKey: ['parents', 'me', 'children'],
    enabled: isParent,
    queryFn: async () => (await api.get('/parents/me/children')).data,
  });

  const upcomingMeets = (meetsQuery.data ?? [])
    .filter((m) => dayjs(m.end_date).isAfter(dayjs().subtract(1, 'day')))
    .slice(0, 5);

  const students = studentsQuery.data ?? [];
  const meets = meetsQuery.data ?? [];
  const parentCoverage = students.filter((s) => s.parents.length > 0).length;
  const meetToPrepare = meets.find((m) => (m.event_count ?? 0) === 0 || (m.registration_count ?? 0) === 0) ?? meets[0];
  const hasEvents = meets.some((m) => (m.event_count ?? 0) > 0);
  const hasEntries = meets.some((m) => (m.registration_count ?? 0) > 0);
  const activationLoading = !isParent && (meetsQuery.isLoading || studentsQuery.isLoading);
  const showActivation = !isParent && !activationLoading && (
    students.length === 0 ||
    parentCoverage < students.length ||
    meets.length === 0 ||
    !hasEvents ||
    !hasEntries
  );
  const activationSteps = [
    { label: 'Add athletes', done: students.length > 0, detail: students.length ? `${students.length} on roster` : 'Start with your roster' },
    {
      label: 'Add parent emails',
      done: students.length > 0 && parentCoverage === students.length,
      detail: students.length ? `${parentCoverage}/${students.length} covered` : 'Needed for schedule emails',
    },
    { label: 'Create a meet', done: meets.length > 0, detail: meets.length ? `${meets.length} meet${meets.length === 1 ? '' : 's'}` : 'Dates and venue' },
    { label: 'Add events', done: hasEvents, detail: hasEvents ? 'Event list started' : 'Use a template' },
    { label: 'Enter athletes', done: hasEntries, detail: hasEntries ? 'Entries saved' : 'Assign athletes to events' },
  ];
  const nextAction = getNextAction({ students, parentCoverage, meets, meetToPrepare, hasEvents, hasEntries });

  if (isParent) {
    return (
      <Box>
        <PageHeader
          title={`Hi ${user?.first_name || 'there'}`}
          subtitle="Here's what's coming up for your athletes."
        />
        <Grid container spacing={2}>
          {(childrenQuery.data ?? []).map((child) => (
            <Grid item xs={12} md={6} key={child.id}>
              <ChildSchedulePreview studentId={child.id} name={child.full_name} />
            </Grid>
          ))}
          {childrenQuery.data && childrenQuery.data.length === 0 && (
            <Grid item xs={12}>
              <Card><CardContent>No children linked to your account yet.</CardContent></Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={`Hi ${user?.first_name || 'there'}`}
        subtitle={showActivation ? "Here's the shortest path to your first useful meet." : 'Manage meets, registrations, and parent communications.'}
      />

      {showActivation && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                  First meet setup
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  {nextAction.title}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
                  {nextAction.description}
                </Typography>
              </Box>

              {students.length > 0 && parentCoverage === 0 && (
                <Alert severity="warning">
                  Parent schedule emails need parent emails linked to athletes. Right now none of your athletes have one.
                </Alert>
              )}

              <Grid container spacing={1.5}>
                {activationSteps.map((step) => (
                  <Grid item xs={12} sm={6} md key={step.label}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          {step.done ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                          )}
                          <Box>
                            <Typography variant="subtitle2">{step.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{step.detail}</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button component={RouterLink} to={nextAction.to} variant="contained" size="large" aria-label={nextAction.ariaLabel}>
                  {nextAction.label}
                </Button>
                <Button component={RouterLink} to="/setup" variant="outlined" size="large" aria-label="Open setup wizard">
                  Open setup wizard
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard
          icon={<EventIcon />}
          label="Upcoming meets"
          value={upcomingMeets.length}
          to="/meets"
        />
        <StatCard
          icon={<SchoolIcon />}
          label="Athletes"
          value={(studentsQuery.data ?? []).length}
          to="/students"
        />
        <StatCard
          icon={<StadiumIcon />}
          label="Total meets"
          value={(meetsQuery.data ?? []).length}
          to="/meets"
        />
        <StatCard
          icon={<EmailIcon />}
          label="Emails sent"
          value={(notifsQuery.data ?? []).filter((n) => n.status === 'sent').length}
          to="/notifications"
        />
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Upcoming meets</Typography>
          {upcomingMeets.length === 0 ? (
            <Typography color="text.secondary">No upcoming meets yet. Create a meet, then prepare events and entries.</Typography>
          ) : (
            <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
              {upcomingMeets.map((m) => (
                <Box
                  key={m.id}
                  component={RouterLink}
                  to={`/meets/${m.id}`}
                  aria-label={`Open meet ${m.name}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { bgcolor: 'action.hover' },
                    px: 1,
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">{m.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dayjs(m.start_date).format('ddd, MMM D')}
                      {m.venue?.name ? ` · ${m.venue.name}` : ''}
                    </Typography>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {m.event_count ?? 0} events · {m.registration_count ?? 0} entries
                    </Typography>
                    {((m.event_count ?? 0) === 0 || (m.registration_count ?? 0) === 0) && (
                      <Chip size="small" color="warning" variant="outlined" label="Needs prep" />
                    )}
                    <Button
                      component={RouterLink}
                      to={`/meets/${m.id}/prepare`}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Prepare
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function getNextAction({
  students,
  parentCoverage,
  meets,
  meetToPrepare,
  hasEvents,
  hasEntries,
}: {
  students: Student[];
  parentCoverage: number;
  meets: Meet[];
  meetToPrepare?: Meet;
  hasEvents: boolean;
  hasEntries: boolean;
}) {
  if (students.length === 0) {
    return {
      title: 'Add your roster first',
      description: 'Once athletes are in Baton, you can add parent emails, create a meet, and assign entries.',
      label: 'Add athletes',
      to: '/students',
      ariaLabel: 'Add athletes',
    };
  }
  if (parentCoverage < students.length) {
    return {
      title: 'Add parent emails before sending schedules',
      description: 'Coach packs work now, but parent emails only send to athletes with linked parent emails.',
      label: 'Fix parent coverage',
      to: '/students',
      ariaLabel: 'Fix parent coverage',
    };
  }
  if (meets.length === 0) {
    return {
      title: 'Create your first meet',
      description: 'A meet gives you the place to add events, assign entries, print a coach pack, and email parents.',
      label: 'Create a meet',
      to: '/meets',
      ariaLabel: 'Create a meet',
    };
  }
  if (!hasEvents || !hasEntries) {
    return {
      title: 'Prepare your meet',
      description: 'Add event templates, check athlete entries, then preview parent emails and print the coach pack.',
      label: 'Prepare meet',
      to: meetToPrepare ? `/meets/${meetToPrepare.id}/prepare` : '/meets',
      ariaLabel: 'Prepare meet',
    };
  }
  return {
    title: 'Preview parent emails',
    description: 'Your meet has entries and parent coverage. Preview the schedule emails before sending.',
    label: 'Preview parent emails',
    to: meetToPrepare ? `/notifications?meet=${meetToPrepare.id}` : '/notifications',
    ariaLabel: 'Preview parent emails',
  };
}

function StatCard({
  icon,
  label,
  value,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  to: string;
}) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card>
        <CardActionArea component={RouterLink} to={to} aria-label={`Go to ${label}`}>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ color: 'primary.main' }}>{icon}</Box>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {value}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
}

function ChildSchedulePreview({ studentId, name }: { studentId: number; name: string }) {
  const q = useQuery<{ entries: ScheduleEntry[] }>({
    queryKey: ['schedule', 'student', studentId],
    queryFn: async () => (await api.get(`/schedule/student/${studentId}`)).data,
  });
  const next = q.data?.entries?.slice(0, 3) ?? [];
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>{name}</Typography>
        {next.length === 0 ? (
          <Typography color="text.secondary">No upcoming events.</Typography>
        ) : (
          <Stack spacing={1}>
            {next.map((e) => (
              <Box key={e.registration_id} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5 }}>
                <Typography variant="subtitle2">
                  {e.event_name} · {e.meet_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.scheduled_start_at ? dayjs(e.scheduled_start_at).format('ddd MMM D, h:mm A') : 'Time TBD'}
                  {e.area ? ` · ${e.area}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
